import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../../shared/components/Button';
import { InlineAlert } from '../../../shared/components/InlineAlert';
import { describeError } from '../describeError';
import { useIsMounted } from '../hooks/useIsMounted';
import { validateImportFile } from '../fileValidation';
import { formatFileSize, sourceSystemLabel } from '../format';
import { payrollHistoryImportGateway } from '../index';
import { providerOf } from '../providerLookup';
import { NEW_IMPORT_PATH, batchStepPath, newImportPath } from '../routes';
import type { BatchDetail, Capabilities } from '../types';
import { ErrorSummary } from './basics';
import { FileDropzone, FileRequirements } from './intake';

type Phase = 'idle' | 'working' | 'done';

function scanText(batch: BatchDetail): string {
  switch (batch.scanStatus) {
    case 'Passed': return batch.scanDetail ? `Passed. ${batch.scanDetail}` : 'Passed';
    case 'Failed': return batch.scanDetail ? `Failed. ${batch.scanDetail}` : 'Failed';
    case 'NotPerformed': return 'Not performed';
    default: return 'Not started';
  }
}

function validationText(batch: BatchDetail): string {
  if (batch.scanStatus === 'Failed') return 'Not run, because the security scan failed';
  switch (batch.status) {
    case 'ColumnMappingRequired': return 'Waiting for you to match your columns';
    case 'ValidationFailed': return `Validation failed. ${batch.errorCount} blocking ${batch.errorCount === 1 ? 'error' : 'errors'} found`;
    case 'Failed': return 'Not completed';
    case 'ReadyForReview': return batch.warningCount > 0 ? `Passed with ${batch.warningCount} ${batch.warningCount === 1 ? 'warning' : 'warnings'}` : 'Passed';
    case 'Created': return 'Not started';
    default: return batch.status;
  }
}

function StatusLines({ phase, batch }: { phase: Phase; batch: BatchDetail }) {
  const working = phase === 'working';
  const scanFailed = batch.scanStatus === 'Failed';
  const waiting = batch.status === 'ColumnMappingRequired';
  const lines: { label: string; icon: string; text: string }[] = [
    { label: 'Uploading', icon: working ? '◔' : '✓', text: working ? 'In progress…' : `Complete${batch.fileName ? `: ${batch.fileName}` : ''}` },
    { label: 'Security scan', icon: working ? '◔' : scanFailed ? '✕' : '✓', text: working ? 'In progress…' : scanText(batch) },
    {
      label: 'Validation',
      icon: working ? '◔' : batch.status === 'ValidationFailed' || batch.status === 'Failed' ? '✕' : waiting ? '○' : '✓',
      text: working ? 'In progress…' : validationText(batch),
    },
  ];
  return (
    <div>
      {working && <progress className="phi-progress" aria-label="Upload, security scan and validation in progress" />}
      <ul className="phi-status-lines" aria-label="Upload progress">
        {lines.map((l) => (
          <li key={l.label}>
            <span aria-hidden="true">{l.icon}</span>
            <strong>{l.label}</strong>
            <span>{l.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface Props {
  batch: BatchDetail;
  capabilities: Capabilities;
  announce: (message: string) => void;
  onBatchChange: (batch: BatchDetail) => void;
}

export function UploadStep({ batch, capabilities, announce, onBatchChange }: Props) {
  const mounted = useIsMounted();
  const [file, setFile] = useState<File | null>(null);
  const [clientError, setClientError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>(batch.status === 'Created' ? 'idle' : 'done');

  const provider = providerOf(batch, capabilities);
  const acceptedFileTypes = provider?.acceptedFileTypes ?? ['.zip'];
  const guided = provider !== null && provider.supportLevel !== 'NativeTemplate';
  const template = provider?.id === 'EpnStandardTemplate';

  const alreadyUploaded = batch.status !== 'Created';
  const scanFailed = batch.scanStatus === 'Failed';
  const canContinue = alreadyUploaded && !scanFailed && ['ColumnMappingRequired', 'ValidationFailed', 'ReadyForReview'].includes(batch.status);

  const choose = (files: FileList | null) => {
    const picked = files?.[0] ?? null;
    setUploadError(null);
    if (!picked) return;
    // Client-side checks are a convenience only; the server enforces type and size.
    const problem = validateImportFile(picked, capabilities.maxUploadBytes, acceptedFileTypes);
    if (problem) {
      setFile(null);
      setClientError(problem);
      return;
    }
    setClientError(null);
    setFile(picked);
  };

  const upload = async () => {
    if (!file) return;
    setPhase('working');
    setUploadError(null);
    announce('Uploading, scanning and validating your file. This can take a moment.');
    try {
      const result = await payrollHistoryImportGateway.uploadFile(batch.id, file);
      if (!mounted.current) return;
      onBatchChange(result);
      setPhase('done');
      announce(
        result.scanStatus === 'Failed'
          ? 'The security scan failed. The file was rejected.'
          : result.status === 'ColumnMappingRequired'
            ? 'Upload complete. Next, check what we found and tell EPN what each column means.'
            : result.status === 'ValidationFailed'
              ? `Upload complete. Validation found ${result.errorCount} blocking errors.`
              : 'Upload complete. The file is ready for review.',
      );
    } catch (err) {
      if (!mounted.current) return;
      setPhase('idle');
      setUploadError(describeError(err, 'The upload failed.'));
    }
  };

  const error = clientError ?? uploadError;

  return (
    <div>
      {!alreadyUploaded && (
        <>
          {guided ? (
            <p>
              Upload a spreadsheet (.xlsx) or CSV file{provider && provider.id !== 'OtherGuided' ? ` exported from ${provider.displayName}` : ''}.
              The file is scanned for security first. In a later step you tell EPN what each column means.
            </p>
          ) : template ? (
            <p>Upload your completed EPN Standard Payroll History Template (.xlsx). The file is scanned for security and validated before anything is imported.</p>
          ) : (
            <p>Upload the .zip export from Escochex Payroll. The file is scanned for security and validated before anything is imported.</p>
          )}
          {error && <ErrorSummary message={error} />}
          <FileRequirements maxUploadBytes={capabilities.maxUploadBytes} {...(provider ? { provider } : {})} />
          <FileDropzone disabled={phase === 'working'} onFiles={choose} acceptedFileTypes={acceptedFileTypes} />
          {file && (
            <dl className="phi-facts phi-file-summary" aria-label="Selected file">
              <dt>File name</dt><dd>{file.name}</dd>
              <dt>Size</dt><dd>{formatFileSize(file.size)}</dd>
              <dt>Source platform</dt><dd>{sourceSystemLabel(batch.sourceSystem)}</dd>
            </dl>
          )}
          {phase === 'working' && <StatusLines phase="working" batch={batch} />}
          <div className="phi-actions">
            <Button type="button" onClick={() => void upload()} disabled={!file} loading={phase === 'working'} loadingLabel="Uploading and validating…">
              Upload and validate
            </Button>
          </div>
        </>
      )}

      {alreadyUploaded && (
        <>
          <dl className="phi-facts phi-file-summary" aria-label="Uploaded file">
            <dt>File name</dt><dd>{batch.fileName ?? '—'}</dd>
            <dt>Size</dt><dd>{formatFileSize(batch.fileSizeBytes)}</dd>
            <dt>Source platform</dt><dd>{sourceSystemLabel(batch.sourceSystem)}</dd>
          </dl>
          <StatusLines phase="done" batch={batch} />
          {scanFailed && (
            <>
              <InlineAlert>{batch.failureReason ?? 'The file failed the security scan and was rejected.'}</InlineAlert>
              <p><Link to={NEW_IMPORT_PATH}>Start a new import</Link></p>
            </>
          )}
        </>
      )}

      <div className="phi-footer">
        {alreadyUploaded ? (
          <span />
        ) : (
          <Link to={newImportPath(provider?.id ?? batch.sourceProvider)} className="phi-btn-secondary">Back</Link>
        )}
        {canContinue && (
          <Link to={batchStepPath(batch.id, 'found')} className="phi-btn-primary">Continue to Check what we found</Link>
        )}
      </div>
    </div>
  );
}
